// Phase 2 — Bulk-import summary modal extracted from Merchants.jsx.
// Splits inserted vs duplicates vs errors after every CSV import,
// surfaces a "Download duplicates" CSV for reconciliation.

import { Btn, Modal } from "@/components/Shared";
import { Upload } from "lucide-react";

export default function MerchantImportReportModal({ report, onClose, onDownloadDuplicates }) {
  return (
    <Modal
      open={!!report}
      onClose={onClose}
      title="Import summary"
      testid="import-report-modal"
      size="lg"
    >
      {report && (
        <div className="space-y-4">
          <div className="text-[12px] text-[var(--muted)]">
            From <b className="text-[var(--ink)]">{report.filename}</b>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="border border-[var(--line-2)] rounded-sm p-3 bg-emerald-50/60" data-testid="import-stat-inserted">
              <div className="text-[10.5px] uppercase tracking-wider text-[var(--muted)] font-semibold">Inserted</div>
              <div className="text-2xl font-semibold text-emerald-800 mt-0.5">{report.inserted}</div>
              <div className="text-[10.5px] text-[var(--muted)] mt-0.5">New merchant rows</div>
            </div>
            <div className="border border-[var(--line-2)] rounded-sm p-3 bg-amber-50/60" data-testid="import-stat-duplicates">
              <div className="text-[10.5px] uppercase tracking-wider text-[var(--muted)] font-semibold">Duplicates skipped</div>
              <div className="text-2xl font-semibold text-amber-800 mt-0.5">{report.duplicates?.length || 0}</div>
              <div className="text-[10.5px] text-[var(--muted)] mt-0.5">merchant_id already exists</div>
            </div>
            <div className="border border-[var(--line-2)] rounded-sm p-3 bg-rose-50/60" data-testid="import-stat-errors">
              <div className="text-[10.5px] uppercase tracking-wider text-[var(--muted)] font-semibold">Errors</div>
              <div className="text-2xl font-semibold text-rose-800 mt-0.5">{report.errors?.length || 0}</div>
              <div className="text-[10.5px] text-[var(--muted)] mt-0.5">Missing ID / bad rate</div>
            </div>
          </div>

          {report.duplicates?.length > 0 && (
            <div className="border border-[var(--line-2)] rounded-sm">
              <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--line-2)] bg-[var(--surface-2)]">
                <div className="text-[11.5px] uppercase tracking-wider text-[var(--muted)] font-semibold">
                  Duplicate merchant IDs — not imported
                </div>
                <Btn variant="ghost" testid="download-duplicates-btn" onClick={onDownloadDuplicates}>
                  <Upload size={12} style={{ transform: "rotate(180deg)" }} /> Download duplicates CSV
                </Btn>
              </div>
              <div className="max-h-64 overflow-auto">
                <table className="table-data w-full">
                  <thead>
                    <tr>
                      <th className="w-12">Row</th>
                      <th>Merchant ID</th>
                      <th>Name in CSV</th>
                      <th>Existing record</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.duplicates.map((d) => (
                      <tr key={`${d.row}-${d.merchant_id}`} data-testid={`dup-row-${d.merchant_id}`}>
                        <td className="text-[11.5px]">{d.row}</td>
                        <td className="font-medium">{d.merchant_id}</td>
                        <td className="text-[11.5px]">{d.merchant_name_csv || "—"}</td>
                        <td className="text-[11.5px] text-[var(--muted)]">
                          {d.existing_name
                            ? <>{d.existing_name}{(d.existing_gateway || d.existing_lob) ? ` · ${d.existing_gateway || "—"} / ${d.existing_lob || "—"}` : ""}</>
                            : "—"}
                        </td>
                        <td className="text-[11px] text-[var(--muted)]">{d.source}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {report.errors?.length > 0 && (
            <div className="border border-[var(--line-2)] rounded-sm">
              <div className="px-3 py-2 border-b border-[var(--line-2)] bg-[var(--surface-2)] text-[11.5px] uppercase tracking-wider text-[var(--muted)] font-semibold">
                Row errors
              </div>
              <ul className="max-h-40 overflow-auto divide-y divide-[var(--line)]" data-testid="import-errors-list">
                {report.errors.map((err, i) => (
                  <li key={`err-${i}-${(err || "").slice(0, 24)}`} className="px-3 py-1.5 text-[11.5px] text-rose-800">{err}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex justify-end pt-1">
            <Btn variant="accent" testid="import-report-close" onClick={onClose}>Done</Btn>
          </div>
        </div>
      )}
    </Modal>
  );
}
